import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Bell, FilePlus2, MessageSquare, MessageCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_ICONS = {
  new_resource: { icon: FilePlus2, class: 'bg-[#34657f]' },
  forum_post: { icon: MessageSquare, class: 'bg-[#3d7a22]' },
  forum_comment: { icon: MessageCircle, class: 'bg-[#b34e00]' },
};

const relTime = (iso) => {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${Math.max(mins, 1)}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

export const NotificationsBell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await axios.get(`${API}/notifications`, { params: { user_id: user.id } });
      setItems(res.data.items || []);
      setUnread(res.data.unread_count || 0);
    } catch (e) { /* silent */ }
  }, [user?.id]);

  useEffect(() => {
    fetchNotifications();
    const iv = setInterval(fetchNotifications, 60000);
    return () => clearInterval(iv);
  }, [fetchNotifications]);

  const onOpenChange = async (next) => {
    setOpen(next);
    if (next && unread > 0) {
      setUnread(0);
      try {
        await axios.post(`${API}/notifications/seen`, null, { params: { user_id: user?.id } });
      } catch (e) { /* silent */ }
    }
  };

  return (
    <DropdownMenu modal={false} open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
          className="relative text-slate-600 hover:text-[#34657f]"
          data-testid="notifications-btn"
        >
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#b34e00] text-white text-[10px] font-bold flex items-center justify-center"
              data-testid="notifications-unread-badge"
            >
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 p-0" data-testid="notifications-panel">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <p className="font-['Manrope'] font-bold text-[#1A1A1A]">Notifications</p>
          <span className="text-xs text-[#5C5C5C]">last 14 days</span>
        </div>
        <div className="max-h-96 overflow-y-auto py-1">
          {items.length === 0 ? (
            <p className="text-sm text-[#5C5C5C] text-center py-8">
              You're all caught up! New files and forum activity will show here.
            </p>
          ) : (
            items.map((n) => {
              const cfg = TYPE_ICONS[n.type] || TYPE_ICONS.new_resource;
              const Icon = cfg.icon;
              return (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => { setOpen(false); navigate(n.link); }}
                  className="w-full flex items-start gap-3 px-4 py-2.5 text-left hover:bg-[#34657f]/5 transition-colors"
                  data-testid={`notification-${n.id}`}
                >
                  <div className={`w-8 h-8 rounded-lg ${cfg.class} flex items-center justify-center shrink-0 mt-0.5`}>
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-[#1A1A1A] leading-snug">{n.title}</p>
                    <p className="text-xs text-[#5C5C5C] mt-0.5">{relTime(n.ts)}</p>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationsBell;
